import axios from '../axiosInstance';

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function buildDashboardSummaryFromQueries(incompleteResult, completedResult) {
  const incompleteItems = incompleteResult?.items ?? [];
  const completedItems = completedResult?.items ?? [];
  const today = startOfToday();
  const weekStart = new Date(today);
  weekStart.setDate(weekStart.getDate() - 6);

  const stageMap = new Map();
  for (const item of incompleteItems) {
    const stageId = item.stageId ?? item.stage?.id ?? 0;
    const stageDescription = item.stage?.description ?? 'Unknown';
    if (!stageMap.has(stageId)) {
      stageMap.set(stageId, { stageId, stageDescription, count: 0 });
    }
    stageMap.get(stageId).count += 1;
  }

  let completedToday = 0;
  let completedLast7Days = 0;
  for (const item of completedItems) {
    if (!item.transactionDate) continue;
    const d = new Date(item.transactionDate);
    if (d >= today) completedToday += 1;
    if (d >= weekStart) completedLast7Days += 1;
  }

  const recentIncomplete = incompleteItems.slice(0, 8).map((item) => ({
    id: item.id,
    ticketNumber:
      item.load?.ticketNumberOrId
      ?? (item.load?.ticketNumber > 0 ? item.load.ticketNumber : null)
      ?? item.loadId
      ?? item.load?.id
      ?? null,
    vehicleReg: item.load?.vehicleReg ?? null,
    accountName: item.account?.name ?? null,
    commodityName: item.commodity?.name ?? null,
    stageDescription: item.stage?.description ?? null,
    transactionDate: item.transactionDate ?? null,
  }));

  return {
    incompleteTotal: incompleteItems.length,
    incompleteByStage: Array.from(stageMap.values()).sort((a, b) => a.stageId - b.stageId),
    completedToday,
    completedLast7Days,
    recentIncomplete,
    approximate: Boolean(incompleteResult?.hasMore || completedResult?.hasMore),
  };
}

async function getDashboardSummaryFallback(signal = null) {
  const pageSize = 200;
  const [incompleteResult, completedResult] = await Promise.all([
    queryEnabledIncompleteLoads({ pageSize, signal }),
    queryEnabledCompletedLoads({ pageSize, signal }),
  ]);
  return buildDashboardSummaryFromQueries(incompleteResult, completedResult);
}

/**
 * Site dashboard summary: incomplete/completed counts and recent loads.
 * Backend: GET api/Loads/dashboardSummary
 * Falls back to grid queries when the dedicated endpoint is not deployed yet.
 */
export async function getDashboardSummary(signal = null) {
  try {
    const { data } = await axios.get('/Loads/dashboardSummary', { signal });
    return data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 400 || status === 404) {
      return getDashboardSummaryFallback(signal);
    }
    throw err;
  }
}

/**
 * DataGrid-style query for completed loads (keyset pagination, sort, filter).
 * Backend: POST api/Loads/queryEnabled (or Loads/CompletedLoads/queryEnabled)
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledCompletedLoads({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  startDate,
  endDate,
  signal = null,
} = {}) {
  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy: sortBy ?? 'transactionDate',
    sortDir: sortDir ?? 'desc',
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
    startDate: startDate ?? null,
    endDate: endDate ?? null,
  };

  const { data } = await axios.post('/Loads/queryEnabledCompletedLoads', body, { signal });
  return data;
}

/**
 * DataGrid-style query for deleted loads (StageId -1 or -2).
 * Backend: POST api/Loads/queryEnabledDeletedLoads
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledDeletedLoads({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  startDate,
  endDate,
  signal = null,
} = {}) {
  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy: sortBy ?? 'transactionDate',
    sortDir: sortDir ?? 'desc',
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
    startDate: startDate ?? null,
    endDate: endDate ?? null,
  };

  const { data } = await axios.post('/Loads/queryEnabledDeletedLoads', body, { signal });
  return data;
}

/**
 * DataGrid-style query for incomplete/weighing loads (keyset pagination, sort, filter).
 * Backend: POST api/Loads/queryEnabledIncompleteLoads
 * Returns: { items, cursor, hasMore }
 */
export async function queryEnabledIncompleteLoads({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  signal = null,
} = {}) {
  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy: sortBy ?? 'transactionDate',
    sortDir: sortDir ?? 'desc',
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
  };

  const { data } = await axios.post('/Loads/queryEnabledIncompleteLoads', body, { signal });
  return data;
}

/**
 * Create LOAD + LOAD_DETAIL in one request. Frontend sends a single DTO; backend splits into
 * LOAD (vehicleReg, badge, siteId) and LOAD_DETAIL (transTypeId, accountId, commodityId, etc.).
 * payload.createMode: 'weighbridge' | 'manual' | 'saveWithoutWeighing'
 * payload.manualWeight: number (optional, when createMode === 'manual')
 * payload.stageId: number (optional, for specific stage ID - 100 for saveWithoutWeighing, 1 for manual)
 * Returns the created load detail (LoadDetailGetDto).
 */
export async function createLoad(payload, signal = null) {
  const { data } = await axios.post('/Loads/createLoad', payload, { signal });
  return data;
}

/**
 * Save weighing data: creates TALLY_ROLL entry and LOAD_DETAILS_WEIGHTS entry.
 * Used for manual weight entry.
 * @param {object} payload - { loadDetailsId: number, weight: number, reason?: string, updateStageId?: number, wbId?: number, rawData?: string, serialNo?: number }
 * @param {number} payload.loadDetailsId - The load detail ID
 * @param {number} payload.weight - The weight value
 * @param {string} [payload.reason] - Optional reason (for future feature)
 * @param {number} [payload.updateStageId] - Optional: if provided, updates LoadDetail's StageId to this value
 * @param {number} [payload.wbId] - Weighbridge ID from CONFIG_WEIGHING for this stage
 * @param {string} [payload.rawData] - Raw scale payload (live capture)
 * @param {number} [payload.serialNo] - DSD serial from indicator (live capture)
 * @returns {Promise<{ tallyRollId: number, loadDetailsWeightId: number, transTypeId?: number, stageId?: number }>}
 */
export async function saveWeighing(payload, signal = null) {
  const { data } = await axios.post('/Loads/SaveWeighing', payload, { signal });
  return data;
}

/**
 * Get list of weights for a given load detail.
 * Backend: GET api/Loads/getWeights?load_detail_id={id}
 * @param {number} loadDetailsId
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array>}
 */
export async function getWeights(loadDetailsId, signal = null) {
  const { data } = await axios.get('/Loads/getWeights', {
    params: { load_detail_id: loadDetailsId },
    signal,
  });
  return data;
}

/**
 * Audit history for a completed load (LOG_TABLES).
 * Backend: GET api/Loads/{loadDetailId}/history
 */
export async function getLoadHistory(loadDetailId, signal = null) {
  const { data } = await axios.get(`/Loads/${loadDetailId}/history`, { signal });
  return data;
}

/**
 * Fetch a single load detail by ID (for edit form).
 * Backend: GET api/Loads/{loadDetailId}
 * @param {number} loadDetailId - The load detail ID
 * @param {AbortSignal} [signal] - Optional abort signal
 * @param {{ cacheBust?: boolean }} [options] - If cacheBust: true, appends ?_=timestamp to avoid HTTP/cache returning stale data (e.g. after SignalR update).
 * @returns {Promise<LoadDetailGetDto>}
 */
export async function getLoadDetail(loadDetailId, signal = null, options = null) {
  const url = options?.cacheBust ? `/Loads/${loadDetailId}?_=${Date.now()}` : `/Loads/${loadDetailId}`;
  const { data } = await axios.get(url, { signal });
  return data;
}

/**
 * Update an existing LoadDetail.
 * @param {number} loadDetailId - The load detail ID to update
 * @param {object} payload - The update payload (similar to createLoad but without createMode/manualWeight)
 * @param {object} signal - Optional abort signal
 * @returns {Promise<LoadDetailGetDto>}
 */
export async function updateLoadDetail(loadDetailId, payload, signal = null) {
  const { data } = await axios.put(`/Loads/${loadDetailId}`, payload, { signal });
  return data;
}

/**
 * Soft delete a LoadDetail by setting its StageId to -1.
 * @param {number} loadDetailId - The load detail ID to delete
 * @param {string|null} [deleteReason] - Optional reason (persisted to LoadDetail.DeleteReason, max 255 chars).
 *   Sent when the tenant has SeekReasonForDeletion enabled for loads.
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
export async function deleteLoadDetail(loadDetailId, deleteReason = null, signal = null) {
  const params = {};
  if (deleteReason != null && String(deleteReason).trim() !== '') {
    params.deleteReason = String(deleteReason).trim().slice(0, 255);
  }
  await axios.delete(`/Loads/${loadDetailId}`, { params, signal });
}

/**
 * Get saved tare for a vehicle (and optional trailer when tractor).
 * Backend: GET api/Loads/getTare
 */
export async function getTare({ vehicleId = null, vehicleReg = null, trailerReg = null, signal = null } = {}) {
  const params = {};
  if (vehicleId != null) params.vehicle_id = vehicleId;
  if (vehicleReg) params.vehicle_reg = vehicleReg;
  if (trailerReg) params.trailer_reg = trailerReg;
  const { data } = await axios.get('/Loads/getTare', { params, signal });
  return data;
}

/**
 * Apply saved tare as a load weight (creates TALLY_ROLL + LOAD_DETAILS_WEIGHTS).
 * Backend: POST api/Loads/ApplyTare
 */
export async function applyTare({ loadDetailsId, trailerReg = null, updateStageId = null, wbId = null, signal = null } = {}) {
  const { data } = await axios.post('/Loads/ApplyTare', {
    loadDetailsId,
    trailerReg,
    updateStageId,
    ...(wbId != null && wbId > 0 ? { wbId } : {}),
  }, { signal });
  return data;
}

/** Fetch completed loads with optional filters + keyset pagination (kept for route loader) */
export async function getCompletedLoads(filters = {}, signal = null) {
  const params = cleanParams(filters);
  const { data } = await axios.get("/Loads/CompletedLoads", { params, signal });
  // data -> { items: [...], nextId: number|null }
  return data;
}

/** Helper: Date -> "YYYY-MM-DD" for start/end date params */
export function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Remove undefined/null/empty-string so they aren't sent as query params */
function cleanParams(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
}